import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Building2, Crown, DollarSign, Loader2, TrendingUp, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { OccupancyChart, PaymentDistributionChart, RevenueChart, TrafficChart } from '../components/ChartWidgets';
import { toTrends, TrendsCharts } from '../components/PlatformTrendsCharts';
import type { TrendsData } from '../components/PlatformTrendsCharts';

interface PlatformAnalytics {
  totalCondos: number;
  activeCondos: number;
  inactiveCondos: number;
  totalUsers: number;
  totalUnits: number;
  currentMonthRevenue: number;
  topCondosByUsers: Array<{ id: string; name: string; userCount: number }>;
  growth: { newCondos30Days: number; newUsers30Days: number };
}

interface VisitorTraffic { date: string; count: number }
interface PaymentBucket { type?: string; category?: string; count: number; total: number }
interface MonthlyValue { thisMonth: number; lastMonth: number }
interface CondoAnalytics {
  visitorTraffic: VisitorTraffic[];
  paymentDistribution: { byType: PaymentBucket[]; byCategory: PaymentBucket[] };
  occupancy: { totalUnits: number; occupiedUnits: number; occupancyRate: number };
  monthlyComparison: { income: MonthlyValue; expenses: MonthlyValue; visitors: MonthlyValue };
}

// --- Uso del producto (product usage tab) types ---
type UsageDays = 7 | 30;
interface SummaryEvent { event: string; count: number; last_seen?: string; distinct_users?: number }
interface UsageSummary { days: number; events: SummaryEvent[]; total: number }
interface DauPoint { date: string; activeUsers: number }
interface UserUsageRow { userKey: string; totalEvents: number; perFunction: Record<string, number> }
interface UsageData {
  summary: UsageSummary | null;
  users: UserUsageRow[] | null; // null = endpoint unavailable (404) → degrade to summary-only
  dau: DauPoint[];
}

const asNumber = (value: unknown): number => typeof value === 'number' && Number.isFinite(value) ? value : Number(value) || 0;
const asRecord = (value: unknown): Record<string, unknown> => value && typeof value === 'object' ? value as Record<string, unknown> : {};
const unwrap = (value: unknown): Record<string, unknown> => {
  const root = asRecord(value);
  return asRecord(root.data ?? root);
};
const toPlatformAnalytics = (value: unknown): PlatformAnalytics => {
  const data = unwrap(value);
  const growth = asRecord(data.growth);
  const top = Array.isArray(data.topCondosByUsers) ? data.topCondosByUsers : [];
  return {
    totalCondos: asNumber(data.totalCondos), activeCondos: asNumber(data.activeCondos), inactiveCondos: asNumber(data.inactiveCondos),
    totalUsers: asNumber(data.totalUsers), totalUnits: asNumber(data.totalUnits), currentMonthRevenue: asNumber(data.currentMonthRevenue),
    topCondosByUsers: top.map(item => { const row = asRecord(item); return { id: String(row.id ?? ''), name: String(row.name ?? 'Condominio'), userCount: asNumber(row.userCount) }; }),
    growth: { newCondos30Days: asNumber(growth.newCondos30Days), newUsers30Days: asNumber(growth.newUsers30Days) },
  };
};
const toCondoAnalytics = (value: unknown): CondoAnalytics => {
  const data = unwrap(value);
  const distribution = asRecord(data.paymentDistribution);
  const occupancy = asRecord(data.occupancy);
  const comparison = asRecord(data.monthlyComparison);
  const monthly = (entry: unknown): MonthlyValue => { const row = asRecord(entry); return { thisMonth: asNumber(row.thisMonth), lastMonth: asNumber(row.lastMonth) }; };
  const buckets = (entry: unknown): PaymentBucket[] => (Array.isArray(entry) ? entry : []).map(item => { const row = asRecord(item); return { type: row.type == null ? undefined : String(row.type), category: row.category == null ? undefined : String(row.category), count: asNumber(row.count), total: asNumber(row.total) }; });
  return {
    visitorTraffic: (Array.isArray(data.visitorTraffic) ? data.visitorTraffic : []).map(item => { const row = asRecord(item); return { date: String(row.date ?? ''), count: asNumber(row.count) }; }),
    paymentDistribution: { byType: buckets(distribution.byType), byCategory: buckets(distribution.byCategory) },
    occupancy: { totalUnits: asNumber(occupancy.totalUnits), occupiedUnits: asNumber(occupancy.occupiedUnits), occupancyRate: asNumber(occupancy.occupancyRate) },
    monthlyComparison: { income: monthly(comparison.income), expenses: monthly(comparison.expenses), visitors: monthly(comparison.visitors) },
  };
};

const usageDataMaxBars = 60; // keep SVG light for 30-day ranges

const toUsageSummary = (value: unknown): UsageSummary => {
  const data = unwrap(value);
  const events = (Array.isArray(data.events) ? data.events : []).map(item => {
    const row = asRecord(item);
    return {
      event: String(row.event ?? row.name ?? ''),
      count: asNumber(row.count),
      last_seen: row.last_seen == null ? undefined : String(row.last_seen),
      distinct_users: row.distinct_users == null && row.users == null ? undefined : asNumber(row.distinct_users ?? row.users),
    };
  }).filter(ev => ev.event !== '');
  return { days: asNumber(data.days) || 30, events, total: asNumber(data.total) || events.reduce((acc, ev) => acc + ev.count, 0) };
};

const toDau = (value: unknown): DauPoint[] => {
  const data = unwrap(value);
  const raw = Array.isArray(data.dau) ? data.dau : [];
  return raw.map(item => {
    const row = asRecord(item);
    // tolerate active_users / users / count aliases
    return { date: String(row.date ?? row.day ?? ''), activeUsers: asNumber(row.active_users ?? row.users ?? row.count) };
  });
};

const toUserRows = (value: unknown): UserUsageRow[] => {
  const data = unwrap(value);
  const raw = Array.isArray(data.users) ? data.users : Array.isArray(data.by_user) ? data.by_user : Array.isArray(data.per_user) ? data.per_user : [];
  return raw.map(item => {
    const row = asRecord(item);
    const key = String(row.user_key ?? row.key ?? row.user ?? '');
    const total = asNumber(row.total_events ?? row.total ?? row.events ?? row.count);
    let perFunction: Record<string, number> = {};
    const pf = row.per_function ?? row.functions ?? row.events_by_function;
    if (pf && typeof pf === 'object' && !Array.isArray(pf)) {
      perFunction = Object.fromEntries(Object.entries(asRecord(pf)).map(([name, count]) => [name, asNumber(count)]));
    } else if (Array.isArray(pf)) {
      perFunction = Object.fromEntries(pf.map(item2 => { const r2 = asRecord(item2); return [String(r2.event ?? r2.name ?? r2.function ?? ''), asNumber(r2.count ?? r2.total)]; }).filter(([name]) => name !== ''));
    }
    return { userKey: key, totalEvents: total, perFunction };
  }).filter(row => row.userKey !== '').sort((a, b) => b.totalEvents - a.totalEvents);
};

const errorMessage = (error: unknown): string => error instanceof Error ? error.message : 'Error al cargar analíticas';
const fmtCurrency = (value: number, currency = 'USD') => new Intl.NumberFormat('es-MX', { style: 'currency', currency: currency === 'MXN' ? 'MXN' : 'USD', maximumFractionDigits: 0 }).format(value);

// Event name → friendly Spanish label
const eventLabel = (event: string): string => {
  const map: Record<string, string> = {
    'page_view': 'Vista de página', 'login': 'Inicio de sesión', 'logout': 'Cierre de sesión',
    'reserve': 'Reservas', 'reservation_created': 'Reserva creada', 'payment': 'Pagos',
    'announcement_created': 'Anuncio creado', 'announcement_view': 'Anuncios vistos',
    'task_created': 'Tarea creada', 'task_completed': 'Tarea completada',
    'visitor_registered': 'Visitante registrado', 'access_granted': 'Acceso concedido',
    'complaint_created': 'Queja o sugerencia', 'pet_registered': 'Mascota registrada',
    'amenity_view': 'Amenidades vistas', 'calendar_view': 'Calendario visto',
  };
  if (map[event]) return map[event];
  const cleaned = event.replace(/[_-]+/g, ' ').trim();
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

// Stable masked id: never resolves to a real identity.
const maskUserKey = (key: string): string => {
  const tail = key.length > 4 ? key.slice(-4) : key;
  return `usr-••••${tail}`;
};

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  const superAdmin = isSuperAdmin();
  const [platformData, setPlatformData] = useState<PlatformAnalytics | null>(null);
  const [condoData, setCondoData] = useState<CondoAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // --- Uso del producto tab state ---
  const [activeTab, setActiveTab] = useState<'platform' | 'usage'>('platform');
  const [usageDays, setUsageDays] = useState<UsageDays>(7);
  const [usageData, setUsageData] = useState<UsageData | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  // --- Tendencias de plataforma (decision 2-A) state ---
  const [trendsDays, setTrendsDays] = useState<UsageDays>(7);
  const [trendsData, setTrendsData] = useState<TrendsData | null>(null);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [trendsError, setTrendsError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      if (superAdmin) setPlatformData(toPlatformAnalytics((await api.get('/analytics/platform')).data));
      else if (user?.condoId) setCondoData(toCondoAnalytics((await api.get(`/condos/${user.condoId}/analytics`)).data));
      else setError('Sin condominio asignado');
    } catch (caught: unknown) { setError(errorMessage(caught)); }
    finally { setLoading(false); }
  }, [superAdmin, user?.condoId]);
  useEffect(() => { void fetchAnalytics(); }, [fetchAnalytics]);

  const fetchUsage = useCallback(async (days: UsageDays) => {
    setUsageLoading(true); setUsageError(null); setExpandedKeys(new Set());
    try {
      // Promise.allSettled: the per-user endpoint may 404 while the backend lands.
      const [summaryRes, usersRes] = await Promise.allSettled([
        api.get(`/analytics/summary?days=${days}`),
        api.get(`/analytics/users?days=${days}`),
      ]);
      const summary = summaryRes.status === 'fulfilled' ? toUsageSummary(summaryRes.value.data) : null;
      const users = usersRes.status === 'fulfilled' ? toUserRows(usersRes.value.data) : null;
      const dau = usersRes.status === 'fulfilled' ? toDau(usersRes.value.data) : [];
      if (summary === null && users === null) {
        setUsageData(null);
        setUsageError('No se pudo cargar el uso del producto');
      } else {
        setUsageData({ summary, users, dau });
      }
    } catch (caught: unknown) {
      setUsageData(null);
      setUsageError(errorMessage(caught));
    } finally { setUsageLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'usage' && superAdmin && usageData === null && usageError === null && !usageLoading) {
      void fetchUsage(usageDays);
    }
  }, [activeTab, superAdmin, usageData, usageError, usageLoading, usageDays, fetchUsage]);

  const fetchTrends = useCallback(async (days: UsageDays) => {
    setTrendsLoading(true); setTrendsError(null);
    try {
      const parsed = toTrends((await api.get(`/analytics/trends?days=${days}`)).data);
      if (parsed === null) setTrendsError('No se pudieron interpretar los datos de tendencias');
      else setTrendsData(parsed);
    } catch (caught: unknown) {
      setTrendsData(null);
      setTrendsError(errorMessage(caught));
    } finally { setTrendsLoading(false); }
  }, []);

  useEffect(() => {
    if (superAdmin && activeTab === 'platform' && trendsData === null && trendsError === null && !trendsLoading) {
      void fetchTrends(trendsDays);
    }
  }, [superAdmin, activeTab, trendsData, trendsError, trendsLoading, trendsDays, fetchTrends]);

  const changeTrendsDays = (days: UsageDays) => {
    setTrendsDays(days);
    void fetchTrends(days);
  };

  const changeUsageDays = (days: UsageDays) => {
    setUsageDays(days);
    void fetchUsage(days);
  };
  const toggleKey = (key: string) => {
    setExpandedKeys(prev => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  };

  if (loading) return <div className="flex items-center justify-center min-h-full bg-[#1a1a1a]"><Loader2 className="w-8 h-8 text-[#36CCD8] animate-spin" /></div>;
  if (error) return <div className="flex flex-col items-center justify-center min-h-full bg-[#1a1a1a] gap-4"><AlertCircle className="w-12 h-12 text-red-400" /><p className="text-gray-300 text-lg">{error}</p><button onClick={() => void fetchAnalytics()} className="px-4 py-2 bg-[#36CCD8] text-black font-semibold rounded-lg">Reintentar</button></div>;

  return <div className="min-h-full bg-[#1a1a1a] p-6"><div className="max-w-6xl mx-auto">
    <header className="mb-8 flex items-center justify-between"><div className="flex items-center gap-4"><button onClick={() => navigate('/home/dashboard')} className="p-2 text-gray-400 hover:text-white rounded-lg"><ArrowLeft className="w-5 h-5" /></button><div><h1 className="text-3xl font-bold text-[var(--v-text-primary)] flex items-center gap-2"><TrendingUp className="w-7 h-7 text-[#36CCD8]" />Analíticas {superAdmin ? 'de Plataforma' : 'del Condominio'}</h1><p className="text-gray-400">{superAdmin ? 'Ingresos, crecimiento y tendencias de uso de módulos' : 'Ingresos, visitantes, pagos y ocupación'}</p></div></div>{superAdmin && <Crown className="w-6 h-6 text-yellow-400" />}</header>

    {superAdmin && (
      <div className="flex gap-2 mb-6">
        <button onClick={() => setActiveTab('platform')} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'platform' ? 'bg-[#36CCD8] text-black' : 'bg-[#2a2a2a] text-gray-300 border border-[#3a3a3a] hover:text-white'}`}>Plataforma</button>
        <button onClick={() => setActiveTab('usage')} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'usage' ? 'bg-[#36CCD8] text-black' : 'bg-[#2a2a2a] text-gray-300 border border-[#3a3a3a] hover:text-white'}`}>Uso del producto</button>
      </div>
    )}

    {superAdmin && activeTab === 'usage' ? (
      usageLoading ? <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 text-[#36CCD8] animate-spin" /></div>
        : usageError ? <div className="flex flex-col items-center py-16 gap-4"><AlertCircle className="w-10 h-10 text-red-400" /><p className="text-gray-300">{usageError}</p><button onClick={() => { setUsageError(null); void fetchUsage(usageDays); }} className="px-4 py-2 bg-[#36CCD8] text-black font-semibold rounded-lg">Reintentar</button></div>
          : usageData === null ? <EmptyState /> : (
            <div className="space-y-6">
              {/* Days toggle 7/30 */}
              <div className="flex items-center justify-between flex-wrap gap-3">
                <p className="text-gray-400 text-sm">Últimos {usageDays} días</p>
                <div className="flex gap-2">
                  {([7, 30] as UsageDays[]).map(days => (
                    <button key={days} onClick={() => changeUsageDays(days)} className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${usageDays === days ? 'bg-[#36CCD8] text-black' : 'bg-[#2a2a2a] text-gray-300 border border-[#3a3a3a] hover:text-white'}`}>{days} días</button>
                  ))}
                </div>
              </div>

              {usageData.users === null && (
                <div className="bg-[#2a2a2a] rounded-xl border border-[#3a3a3a] p-4 text-sm text-gray-400">
                  Los datos por usuario aún no están disponibles; se muestra el resumen general.
                </div>
              )}

              {/* DAU mini bar chart — dependency-free inline SVG */}
              {usageData.dau.length > 0 ? <DauChart data={usageData.dau} /> : usageData.users !== null ? <div className="bg-[#2a2a2a] rounded-xl border border-[#3a3a3a] p-6 text-gray-400">Sin datos de usuarios activos diarios para este rango</div> : null}

              {/* Funciones más usadas — top 15 */}
              <div className="bg-[#2a2a2a] rounded-xl border border-[#3a3a3a] p-6">
                <h2 className="text-xl font-bold text-[var(--v-text-primary)] mb-4">Funciones más usadas</h2>
                {usageData.summary === null ? <p className="text-gray-400 text-sm">El resumen aún no está disponible.</p>
                  : usageData.summary.events.length === 0 ? <EmptyState /> : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="text-left text-gray-400 border-b border-[#3a3a3a]"><th className="py-2 pr-4">#</th><th className="py-2 pr-4">Función</th><th className="py-2 pr-4 text-right">Eventos</th><th className="py-2 text-right">Usuarios distintos</th></tr></thead>
                        <tbody>
                          {usageData.summary.events.slice(0, 15).map((ev, index) => (
                            <tr key={ev.event} className="border-b border-[#3a3a3a]/50 text-gray-300">
                              <td className="py-2 pr-4 text-gray-500">{index + 1}</td>
                              <td className="py-2 pr-4">{eventLabel(ev.event)}</td>
                              <td className="py-2 pr-4 text-right text-white font-semibold">{ev.count.toLocaleString()}</td>
                              <td className="py-2 text-right">{ev.distinct_users == null ? '—' : ev.distinct_users.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
              </div>

              {/* Uso por usuario — masked keys only, expandable per_function chips */}
              <div className="bg-[#2a2a2a] rounded-xl border border-[#3a3a3a] p-6">
                <h2 className="text-xl font-bold text-[var(--v-text-primary)] mb-4">Uso por usuario</h2>
                {usageData.users === null || usageData.users.length === 0 ? <p className="text-gray-400 text-sm">Sin datos por usuario para este rango.</p> : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="text-left text-gray-400 border-b border-[#3a3a3a]"><th className="py-2 pr-4">#</th><th className="py-2 pr-4">Usuario</th><th className="py-2 pr-4 text-right">Eventos</th><th className="py-2">Funciones principales</th></tr></thead>
                        <tbody>
                          {usageData.users.slice(0, 50).map((row, index) => {
                            const top3 = Object.entries(row.perFunction).sort((a, b) => b[1] - a[1]).slice(0, 3);
                            const expanded = expandedKeys.has(row.userKey);
                            return (
                              <tr key={row.userKey} className="border-b border-[#3a3a3a]/50 align-top">
                                <td className="py-2 pr-4 text-gray-500">{index + 1}</td>
                                <td className="py-2 pr-4">
                                  <button onClick={() => toggleKey(row.userKey)} className="text-[#36CCD8] hover:underline text-left font-mono text-xs">{maskUserKey(row.userKey)}</button>
                                  {expanded && Object.keys(row.perFunction).length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-2 max-w-xs">
                                      {Object.entries(row.perFunction).sort((a, b) => b[1] - a[1]).map(([fn, count]) => (
                                        <span key={fn} className="px-2 py-0.5 rounded-full bg-[#36CCD8]/10 text-[#36CCD8] border border-[#36CCD8]/30 text-xs">{eventLabel(fn)} ×{count}</span>
                                      ))}
                                    </div>
                                  )}
                                </td>
                                <td className="py-2 pr-4 text-right text-white font-semibold">{row.totalEvents.toLocaleString()}</td>
                                <td className="py-2 text-gray-400 text-xs">{top3.length === 0 ? '—' : top3.map(([fn, count]) => `${eventLabel(fn)} ×${count}`).join(' · ')}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {usageData.users.length > 50 && <p className="text-gray-500 text-xs mt-3">Mostrando 50 de {usageData.users.length} usuarios.</p>}
                  </>
                )}
              </div>
            </div>
          )
    ) : (
      <>
        {superAdmin && platformData ? <><div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8"><StatCard icon={<Building2 />} label="Condominios" value={platformData.totalCondos.toLocaleString()} sub={`${platformData.activeCondos} activos`} /><StatCard icon={<DollarSign />} label="Ingresos del mes" value={fmtCurrency(platformData.currentMonthRevenue)} /><StatCard icon={<Users />} label="Usuarios" value={platformData.totalUsers.toLocaleString()} sub={`${platformData.totalUnits} unidades`} /></div><div className="bg-[#2a2a2a] rounded-xl border border-[#3a3a3a] p-6"><h2 className="text-xl font-bold text-[var(--v-text-primary)] mb-4">Top condominios por usuarios</h2>{platformData.topCondosByUsers.length === 0 ? <EmptyState /> : platformData.topCondosByUsers.map(row => <div key={row.id} className="flex justify-between text-gray-300 py-2"><span>{row.name}</span><span>{row.userCount.toLocaleString()}</span></div>)}</div><div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-8"><StatCard icon={<Building2 />} label="Nuevos condominios (30d)" value={platformData.growth.newCondos30Days.toLocaleString()} sub="Altas en los últimos 30 días" /><StatCard icon={<Users />} label="Nuevos usuarios (30d)" value={platformData.growth.newUsers30Days.toLocaleString()} sub="Registros en los últimos 30 días" /><StatCard icon={<DollarSign />} label="MRR estimado" value={fmtCurrency(platformData.currentMonthRevenue)} sub="Ingresos del mes en curso" /></div><TrendsBlock days={trendsDays} data={trendsData} loading={trendsLoading} error={trendsError} onDaysChange={changeTrendsDays} onRetry={() => void fetchTrends(trendsDays)} /></> : condoData ? <><div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8"><StatCard icon={<Building2 />} label="Ocupación" value={`${condoData.occupancy.occupancyRate.toFixed(1)}%`} sub={`${condoData.occupancy.occupiedUnits}/${condoData.occupancy.totalUnits} unidades`} /><StatCard icon={<DollarSign />} label="Ingresos del mes" value={fmtCurrency(condoData.monthlyComparison.income.thisMonth)} sub="Comparación mensual" /><StatCard icon={<Users />} label="Visitantes del mes" value={condoData.monthlyComparison.visitors.thisMonth.toLocaleString()} /></div><div className="grid grid-cols-1 lg:grid-cols-2 gap-6"><RevenueChart data={[{ month: 'Anterior', revenue: condoData.monthlyComparison.income.lastMonth, expenses: condoData.monthlyComparison.expenses.lastMonth }, { month: 'Actual', revenue: condoData.monthlyComparison.income.thisMonth, expenses: condoData.monthlyComparison.expenses.thisMonth }]} /><TrafficChart data={condoData.visitorTraffic.map(item => ({ day: item.date, visitors: item.count }))} /><PaymentDistributionChart data={condoData.paymentDistribution.byType.map(item => ({ name: item.type ?? 'Sin tipo', value: item.total }))} /><OccupancyChart data={[{ unit: 'Unidades', occupied: condoData.occupancy.occupiedUnits, available: Math.max(0, condoData.occupancy.totalUnits - condoData.occupancy.occupiedUnits) }]} /></div></> : <EmptyState />}
      </>
    )}
  </div></div>;
};

const DauChart = ({ data }: { data: DauPoint[] }) => {
  const width = 720;
  const height = 140;
  const padding = { top: 10, right: 8, bottom: 18, left: 8 };
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;
  const points = data.slice(-usageDataMaxBars);
  const max = Math.max(1, ...points.map(point => point.activeUsers));
  const barGap = 2;
  const barWidth = points.length > 0 ? Math.max(2, (innerWidth - barGap * (points.length - 1)) / points.length) : 0;
  return <div className="bg-[#2a2a2a] rounded-xl border border-[#3a3a3a] p-6">
    <h2 className="text-xl font-bold text-[var(--v-text-primary)] mb-4">Usuarios activos diarios</h2>
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" role="img" aria-label="Usuarios activos diarios">
      {points.map((point, index) => {
        const barHeight = max > 0 ? (point.activeUsers / max) * innerHeight : 0;
        const x = padding.left + index * (barWidth + barGap);
        const y = padding.top + innerHeight - barHeight;
        return <g key={point.date || index}>
          <rect x={x} y={barHeight > 0 ? y : padding.top + innerHeight - 1} width={barWidth} height={Math.max(1, barHeight)} rx={2} fill="#36CCD8" opacity={0.85} />
        </g>;
      })}
      {points.length > 0 && <text x={padding.left} y={height - 4} fill="#9ca3af" fontSize={10}>{points[0].date}</text>}
      {points.length > 1 && <text x={width - padding.right} y={height - 4} fill="#9ca3af" fontSize={10} textAnchor="end">{points[points.length - 1].date}</text>}
    </svg>
    <p className="text-gray-500 text-xs mt-2">Máximo del período: {max.toLocaleString()} usuarios activos</p>
  </div>;
};
const TrendsBlock = ({ days, data, loading, error, onDaysChange, onRetry }: { days: UsageDays; data: TrendsData | null; loading: boolean; error: string | null; onDaysChange: (days: UsageDays) => void; onRetry: () => void }) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between flex-wrap gap-3">
      <h2 className="text-xl font-bold text-[var(--v-text-primary)]">Tendencias de actividad</h2>
      <div className="flex gap-2">
        {([7, 30] as UsageDays[]).map(d => (
          <button key={d} onClick={() => onDaysChange(d)} className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${days === d ? 'bg-[#36CCD8] text-black' : 'bg-[#2a2a2a] text-gray-300 border border-[#3a3a3a] hover:text-white'}`}>{d} días</button>
        ))}
      </div>
    </div>
    {loading ? (
      <div className="flex items-center justify-center py-16 bg-[#2a2a2a] rounded-xl border border-[#3a3a3a]"><Loader2 className="w-8 h-8 text-[#36CCD8] animate-spin" /></div>
    ) : error ? (
      <div className="flex flex-col items-center py-10 gap-3 bg-[#2a2a2a] rounded-xl border border-[#3a3a3a]">
        <AlertCircle className="w-10 h-10 text-red-400" />
        <p className="text-gray-300 text-sm">No se pudieron cargar las tendencias: {error}</p>
        <button onClick={onRetry} className="px-4 py-2 bg-[#36CCD8] text-black font-semibold rounded-lg">Reintentar</button>
      </div>
    ) : data ? <TrendsCharts data={data} days={days} /> : null}
  </div>
);
const EmptyState = () => <div className="text-center py-12 text-gray-400">Sin datos para el rango seleccionado</div>;
const StatCard = ({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) => <div className="bg-[#2a2a2a] rounded-xl border border-[#3a3a3a] p-5"><div className="text-[#36CCD8] mb-3">{icon}</div><p className="text-gray-400 text-sm">{label}</p><p className="text-2xl font-bold text-white">{value}</p>{sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}</div>;
export default AnalyticsPage;
